import {
  TextField,
  Card,
  Typography,
  InputAdornment,
  Box,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { AutoAwesome } from "@mui/icons-material";
import { useChatApi } from "../../hooks/useChatApi/useChatApi";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";

export const DashboardSearch = () => {
  const [inputValue, setInputValue] = useState("");
  const [conversation, setConversation] = useState<
    { role: string; content: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { apiUrl, auth, companyId, theme } = useAccelContext();

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setIsLoading(true);
      const newUserMessage = { role: "USER", content: inputValue };
      setConversation((prev) => [...prev, newUserMessage]);

      try {
        const response = await fetch(
          `${apiUrl}/${companyId}/transactions/prompt?prompt=${inputValue}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.access_token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
      

        const messageContent = data.response;
        const newAssistantMessage = {
          role: "ASSISTANT",
          content: messageContent,
        };
        setConversation((prev) => [...prev, newAssistantMessage]);
      } catch (error) {
        console.error("Error getting response:", error);
      } finally {
        setIsLoading(false);
      }
      setInputValue("");
    }
  };

  const hasMessages = conversation.length > 0;

  return (
    <div>
      {!hasMessages ? (
        <Box sx={{ width: "100%" }}>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Helvetica Neue",
              fontSize: "16px",
              color: theme?.secondary?.hex || "#000000",
            }}
          >
            You can ask...
          </Typography>
          <Box sx={{ display: "flex", alignItems: "right", marginTop: 1 }}>
            <AutoAwesome
              sx={{ color: theme?.primary?.hex || "#000000", marginRight: 1 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Helvetica Neue",
                fontSize: "14px",

                color: theme?.text?.hex || "#000000",
              }}
            >
              <span style={{ color: theme?.primary?.hex || "#000000" }}>
                "Profit change this month"
              </span>
              <br />
              Profit increased 43% MoM
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", marginTop: 1 }}>
            <AutoAwesome
              sx={{ color: theme?.primary?.hex || "#000000", marginRight: 1 }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Helvetica Neue",
                fontSize: "14px",
                color: theme?.text?.hex || "#000000",
                textAlign: "left",
              }}
            >
              <span style={{ color: theme?.primary?.hex || "#000000" }}>
                "Any unusual changes to finances this year?"
              </span>
              <br />
              Subscription expenses experienced a sudden spike in q1 2024. 90%
              of increase was from twilio inc.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowY: "auto", maxHeight: 150 }}>
          {conversation.map((message, index) => (
            <React.Fragment key={index}>
              <Box
                display="flex"
                justifyContent={
                  message.role === "USER" ? "flex-end" : "flex-start"
                }
                m={2}
                position="relative"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    backgroundColor: theme?.accent?.hex || "#000000",
                    p: 2,
                    borderRadius: "8px",
                    maxWidth: "70%",
                  }}
                >
                  {message.role !== "USER" && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: theme?.primary?.hex || "#000000",
                        marginRight: 2,
                        marginTop: 1,
                        flexShrink: 0,
                      }}
                    />
                  )}

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "14px",
                      lineHeight: 1.6,
                      color: theme?.secondary?.hex || "#000000", // Changed text color to white
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </Box>
      )}
      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <TextField
          id="search-bar"
          variant="outlined"
          placeholder="Search/talk to your finances"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AutoAwesome sx={{ color: theme?.primary?.hex || "#000000" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme?.primary?.hex || "#000000",
              },
              "&:hover fieldset": {
                borderColor: theme?.primary?.hex || "#000000",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme?.primary?.hex || "#000000",
              },
            },
          }}
        />
      </form>
    </div>
  );
};

export default DashboardSearch;
